import { z } from 'zod';
import { sprompt } from '@epihub/core/src/fn/prompt';
import { parseBoolOrUndefined } from '@epihub/core/src/fn/boolean';
import { parseFloatOrUndefined } from '@epihub/core/src/fn/number';
import { zodQuickResponse } from '../types';
import { sfyr } from '../_ctx/sfyr';
import {
  type AvailableTheme,
  AvailableThemes,
  EAvailableThemes,
  ESupportedLanguages,
  type SupportedLanguage,
  SupportedLanguages,
} from './const';

function defineAction<
  Type extends string = string,
  P extends object = object,
>(
  type: Type,
  opts: {
    schema: z.ZodType<P>;
    description: string;
    useAsAgentTool: boolean;
    useAsPostResponseTool: boolean;
    onSearchParams: (searchParams: URLSearchParams) => NoInfer<P> | null;
  },
) {
  return {
    _: null as any as {
      Payload: P;
    },
    type,
    schema: opts.schema,
    description: opts.description,
    useAsAgentTool: opts.useAsAgentTool,
    useAsPostResponseTool: opts.useAsPostResponseTool,
    onSearchParams: opts.onSearchParams,
  };
}

const updateSoundAction = defineAction('updateSoundAction', {
  schema: z.object({
    soundOn: z.boolean().describe(sprompt`
      When true the sound will be turned on, when false the sound will be turned off
    `),
  }),
  description: sfyr('llm.career_promoter_agent.tools.set_sound.description', {
    defaults: {
      'llm.career_promoter_agent.tools.set_sound.description': `
        Use this tool to turn the sound of the frontend on or off.
        
        If you want to provide the user with a link in your response where they can do this automatically provide
        a link as [Sound on](?sound=true) or [Sound on](?sound=false)
      `,
    },
  }),
  useAsAgentTool: true,
  useAsPostResponseTool: false,
  onSearchParams: (sp) => {
    const soundOn = parseBoolOrUndefined(sp.get('sound'));
    if (soundOn == null) {
      return null;
    }
    sp.delete('sound');
    return { soundOn };
  },
});

const changeLanguageAction = defineAction('changeLanguageAction', {
  schema: z.object({
    language: z.enum(SupportedLanguages).describe(sprompt`
      The new language to use
    `),
  }),
  description: sfyr('llm.career_promoter_agent.tools.change_language.description', {
    defaults: {
      'llm.career_promoter_agent.tools.change_language.description': `
        Use this tool whenever the language must be changed
        For example, if assistant are in english but the user is speaking in italian
        then you should change the language to italian.
        
        If you want to provide the user with a link in your response where they can do this automatically provide
        a link as [Change to Italian](?language=it) or [Change to English](?language=en)
      `,
    },
  }),
  useAsAgentTool: true,
  useAsPostResponseTool: false,
  onSearchParams: (sp) => {
    const language = sp.get('language');
    if (ESupportedLanguages[language as any] == null) {
      return null;
    }
    sp.delete('language');
    return { language: language as SupportedLanguage };
  },
});

const changeThemeAction = defineAction('changeThemeAction', {
  schema: z.object({
    theme: z.enum(AvailableThemes).describe(sprompt`
      The new theme to use
    `),
  }),
  description: sfyr('llm.career_promoter_agent.tools.change_theme.description', {
    defaults: {
      'llm.career_promoter_agent.tools.change_theme.description': `
        Based on the current conversation topic, change the frontend theme accordingly:
        * start: This is the initial theme. Revert back to it only if the conversation shifts from specific to generic.
        * trainer: Use this theme when the discussion explicitly involves personal training, fitness goals, or health-related topics.
        * work: Switch to this theme when the conversation centers around my career as a Software Engineer, including job responsibilities, technologies used, and professional experiences. Additionally, this theme should be used when discussing employment practices and workplace dynamics.
        * projects: Change to this theme when talking about side hustles, personal projects outside of employment, or entrepreneurial ventures related to software or tech.
        * alacarte: Use this theme when discussing the "A La Carte" project specifically, restaurant concepts, or culinary experiences. This theme is a more specific edge-case of "projects."
        * Ensure to maintain awareness of the context to trigger the appropriate theme seamlessly.
      `,
    },
  }),
  useAsAgentTool: true,
  useAsPostResponseTool: true,
  onSearchParams: (sp) => {
    const theme = sp.get('theme');
    if (EAvailableThemes[theme as any] == null) {
      return null;
    }
    sp.delete('theme');
    return { theme: theme as AvailableTheme };
  },
});

const openCalendarAction = defineAction('openCalendarAction', {
  schema: z.object({}),
  description: sfyr('llm.career_promoter_agent.tools.open_calendar.description', {
    defaults: {
      'llm.career_promoter_agent.tools.open_calendar.description': `
        Use this tool to open my calendar on the frontend. 
        
        If you want to provide the user with a link in your response where they can do this automatically provide
        a link as [Open Calendar](?calendar=true)
      `,
    },
  }),
  useAsAgentTool: true,
  useAsPostResponseTool: false,
  onSearchParams: (sp) => {
    const calendar = sp.get('calendar');
    if (calendar == null) {
      return null;
    }
    sp.delete('calendar');
    return {};
  },
});

const setQuickResponsesAction = defineAction('setQuickResponsesAction', {
  schema: z.object({
    quickResponses: z.array(zodQuickResponse),
  }),
  description: sfyr('llm.career_promoter_agent.tools.update_quick_responses.description', {
    defaults: {
      'llm.career_promoter_agent.tools.update_quick_responses.description': `
        Use this tool to provide the user with exactly 3 quick responses of at the most 5 words each
        and a sensible emoji.
        The followups should be completely relevant to your response. 
      `,
    },
  }),
  useAsAgentTool: true,
  useAsPostResponseTool: true,
  onSearchParams: () => null,
});

const updateUsersNameAction = defineAction('updateUsersNameAction', {
  schema: z.object({
    name: z.string().describe(sprompt`
      The name of the user
    `),
  }),
  description: sfyr('llm.career_promoter_agent.tools.set_user_name.description', {
    defaults: {
      'llm.career_promoter_agent.tools.set_user_name.description': `
        When the user tells us his name call this function to update the UI
      `,
    },
  }),
  useAsAgentTool: true,
  useAsPostResponseTool: false,
  onSearchParams: (sp) => {
    const user = sp.get('user');
    if (user == null) {
      return null;
    }
    sp.delete('user');
    return { name: user };
  },
});

const flagMisuseRate = defineAction('flagMisuseRate', {
  schema: z.object({
    misuseRate: z.number(),
  }),
  description: sfyr('llm.career_promoter_agent.tools.misuse_rate.description', {
    defaults: {
      'llm.career_promoter_agent.tools.misuse_rate.description': `
        You are a gatekeeper for Krishan's portfolio website chatbot.
        
        IDENTITY: This chatbot is an interactive "About Me" page.
        It provides INFORMATION about Krishan, his projects, life, interests and his portfolio to visitors.
        It does not provide SERVICES to visitors.
        
        THE ONLY QUESTION THAT MATTERS:
        Is Krishan or his portfolio is the POINT of the user's message, or is the user's own need the POINT?
        - If Krishan is the POINT → LEGITIMATE (confidence 0.0 - 0.3)
        - If Krishan's portfolio is the POINT → LEGITIMATE (confidence 0.0 - 0.3)
        - If Krishan's general life is the POINT → LEGITIMATE (confidence 0.0 - 0.3)
        - If the user's own need is the subject, the user is asking for help completely unrelated to KRISHAN → MISUSE (confidence 0.7 - 1.0)
        
        This chatbot DESCRIBES a person. It does not ACT as that person.
        Visitors may ask what Krishan knows. They may not ask Krishan's chatbot to apply that knowledge on their behalf.
        The field of the question is irrelevant. Whether the topic overlaps with Krishan's expertise is irrelevant. The ONLY relevant factor is whether the user is seeking INFORMATION ABOUT KRISHAN or seeking ASSISTANCE FOR THEMSELVES.
      `,
    },
  }),
  useAsAgentTool: false,
  useAsPostResponseTool: true,
  onSearchParams: (sp) => {
    const misuseRate = parseFloatOrUndefined(sp.get('misuseRate'));
    if (misuseRate == null) {
      return null;
    }
    sp.delete('misuse');
    return { misuseRate };
  },
});

const downloadCV = defineAction('downloadCV', {
  schema: z.object({}),
  description: sfyr('llm.career_promoter_agent.tools.download_cv.description', {
    defaults: {
      'llm.career_promoter_agent.tools.download_cv.description': `
        When the user asks to download my CV call this function
        
        If you want to provide the user with a link in your response where they can do this automatically provide
        a link as [Download CV](?cv=true)
      `,
    },
  }),
  useAsAgentTool: true,
  useAsPostResponseTool: false,
  onSearchParams: (sp) => {
    const cv = parseBoolOrUndefined(sp.get('cv'));
    if (cv == null) {
      return null;
    }
    sp.delete('cv');
    return {};
  },
});

const autoMessage = defineAction('autoMessage', {
  schema: z.object({
    text: z.string(),
  }),
  description: `
    Used internally and not available to any LLM (useAsAgentTool=false) (useAsPostResponseTool=false)
    to trigger auto messages
  `,
  useAsAgentTool: false,
  useAsPostResponseTool: false,
  onSearchParams: (sp) => {
    const autoMessage = sp.get('autoMessage');
    if (autoMessage == null) {
      return null;
    }
    sp.delete('autoMessage');
    return { text: autoMessage };
  },
});

export const ACTIONS = {
  updateSoundAction,
  changeLanguageAction,
  changeThemeAction,
  openCalendarAction,
  setQuickResponsesAction,
  updateUsersNameAction,
  flagMisuseRate,
  downloadCV,
  autoMessage,
};

export const zodActionEvent = z.object({
  type: z.string(),
  actionId: z.string(),
  payload: z.any(),
});
export type ActionEvent = {
  [K in keyof typeof ACTIONS]: {
    type: K;
    actionId: string;
    payload: (typeof ACTIONS)[K]['_']['Payload'];
  };
}[keyof typeof ACTIONS];
